import { uiText } from '../i18n/uiText';
import { notificationText } from '../utils/notificationText';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  Bell,
  Building2,
  CheckCheck,
  ClipboardCheck,
  FileCheck,
  KeyRound,
  Mail,
  MessageSquare,
  RefreshCw,
  ShieldAlert,
} from 'lucide-react';
import PageIntro, { HelpText, QuickActions } from '../components/ui/PageIntro';
import Badge from '../components/ui/Badge';
import { getApiErrorMessage, notificationsApi } from '../api';
import { useAuth } from '../store/AuthContext';
import { formatNotificationTime } from '../utils/formatters';
import { canAccessResource, isAuditeeRole } from '../utils/permissions';
import { safeInternalRoute } from '../utils/safeNavigation';

const KIND_META = {
  all: { label: uiText("Все") },
  assignment: { label: uiText("Назначения"), icon: ClipboardCheck },
  access_request: { label: uiText("Запросы доступа"), icon: KeyRound },
  status: { label: uiText("Статусы"), icon: ShieldAlert },
  comment: { label: uiText("Комментарии"), icon: MessageSquare },
  issue_message: { label: uiText("Замечания"), icon: MessageSquare },
  registration: { label: uiText("Регистрации"), icon: Building2 },
  invitation: { label: uiText("Приглашения"), icon: Mail },
  self_check: { label: 'Self-check', icon: FileCheck },
  erp_flag: { label: 'ERP', icon: AlertTriangle },
  qualification: { label: uiText("Квалификация"), icon: FileCheck },
};

const DEFAULT_FILTERS = ['all', 'access_request', 'assignment', 'status', 'comment', 'issue_message', 'registration', 'invitation', 'self_check', 'erp_flag'];

function resolveNotificationHref(item, isAuditee = false) {
  if (isAuditee && item?.meta?.audit_id && item.kind === 'status') return `/audits/${item.meta.audit_id}`;
  if (isAuditee && item?.kind === 'self_check') return '/self-check';
  if (typeof item?.href === 'string') return safeInternalRoute(item.href, '/notifications');
  if (item?.meta?.audit_id && item.kind === 'comment') return `/audits/${item.meta.audit_id}?tab=comments`;
  if (item?.meta?.audit_id && item.kind === 'status') return isAuditee ? `/audits/${item.meta.audit_id}` : `/audits/${item.meta.audit_id}?tab=history`;
  if (item?.meta?.audit_id && item.kind === 'issue_message') return `/audits/${item.meta.audit_id}?tab=issues`;
  if (item?.kind === 'assignment') return '/assignment-notifications';
  if (item?.kind === 'registration') return '/users';
  if (item?.kind === 'self_check' && item?.meta?.company_id) return isAuditee ? '/self-check' : `/self-check/review/${item.meta.company_id}`;
  return '/notifications';
}

function NotificationRow({ item, onOpen, isAuditee = false }) {
  const Icon = KIND_META[item.kind]?.icon || Bell;
  const destination = resolveNotificationHref(item, isAuditee);
  return (
    <button type="button" className={`workspace-notification-item ${item.unread ? 'is-unread' : ''}`} onClick={() => onOpen(item)}>
      <span className="workspace-notification-kind"><Icon size={15} /></span>
      <div className="workspace-notification-content">
        <div className="workspace-notification-topline">
          <span className="workspace-notification-title">{uiText(item.title)}</span>
          <span className="workspace-notification-date">{formatNotificationTime(item.created_at)}</span>
        </div>
        {item.actor?.full_name ? <div className="workspace-notification-actor">{item.actor.full_name}</div> : null}
        <div className="workspace-notification-text">{notificationText(item) || uiText("Без текста")}</div>
        {destination === '/notifications' ? <div className="workspace-notification-fallback">{uiText("Источник недоступен. Уведомление сохранено в центре уведомлений.")}</div> : null}
      </div>
      {item.unread ? <span className="workspace-notification-dot" /> : null}
    </button>
  );
}

function EmptyState({ unreadOnly, homeTo = '/dashboard', homeLabel = uiText('Открыть обзор') }) {
  return (
    <div className="workspace-empty-state">
      <div className="workspace-empty-state-title">{unreadOnly ? uiText("Непрочитанных уведомлений нет") : uiText("Уведомлений пока нет")}</div>
      <div className="workspace-empty-state-text">{uiText("Когда появится новое назначение, комментарий или изменение статуса, всё соберётся здесь по времени.")}</div>
      <Link className="btn btn-outline btn-sm" to={homeTo}>{homeLabel}</Link>
    </div>
  );
}

export default function NotificationsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canViewAssignments = canAccessResource(user, 'management');
  const isAuditee = isAuditeeRole(user);
  const [kind, setKind] = useState('all');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState({ items: [], groups: [], kind_stats: {}, unread_count: 0 });

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await notificationsApi.list({ limit: 100, unread_only: unreadOnly, kind });
      setData(res.data || { items: [], groups: [], kind_stats: {}, unread_count: 0 });
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [kind, unreadOnly]);

  useEffect(() => { load(); }, [load]);

  const openItem = async (item) => {
    try {
      if (item.unread) {
        await notificationsApi.markRead({ source_type: item.source_type, source_id: item.source_id });
      }
    } catch {
      // navigation is more important than marking read
    }
    navigate(resolveNotificationHref(item, isAuditee));
  };

  const markAllRead = async () => {
    try {
      await notificationsApi.markAllRead();
      await load();
    } catch (err) {
      setError(getApiErrorMessage(err));
    }
  };

  const filterOptions = useMemo(() => {
    const available = Array.isArray(data.available_filters) && data.available_filters.length
      ? data.available_filters
      : DEFAULT_FILTERS;
    return available.filter((item) => item === 'all' || data.kind_stats?.[item]);
  }, [data.available_filters, data.kind_stats]);

  if (isAuditee) {
    return (
      <div className="workspace-page auditee-portal-page auditee-notifications-page">
        <PageIntro
          eyebrow={uiText("Личный кабинет")}
          title={uiText("Уведомления")}
          description={uiText("Сообщения аудитора и изменения по проверкам вашей организации.")}
          aside={(
            <div className="workspace-side-actions">
              <button type="button" className="btn btn-outline" onClick={load} disabled={loading}><RefreshCw size={15} /> {loading ? uiText("Обновляем…") : uiText("Обновить")}</button>
              <button type="button" className="btn btn-primary" onClick={markAllRead} disabled={!data.unread_count}><CheckCheck size={15} /> {data.unread_count ? uiText("Прочитать всё") : uiText("Всё прочитано")}</button>
            </div>
          )}
        />

        {error ? <div className="alert alert-error">{error}</div> : null}

        <section className="auditee-section" aria-labelledby="auditee-notifications-heading">
          <div className="auditee-section-heading">
            <div>
              <h2 id="auditee-notifications-heading">{uiText("Последние события")}</h2>
              <p>{data.unread_count ? uiText("Новых уведомлений: {v0}", {v0: (data.unread_count)}) : uiText("Новых уведомлений нет")}</p>
            </div>
            <label className="workspace-inline-check">
              <input type="checkbox" checked={unreadOnly} onChange={(event) => setUnreadOnly(event.target.checked)} />
              <span>{uiText("Только новые")}</span>
            </label>
          </div>

          {filterOptions.length > 2 ? (
            <div className="auditee-status-filters auditee-notification-filters" aria-label={uiText("Тип уведомлений")}>
              {filterOptions.map((item) => (
                <button type="button" key={item} className={kind === item ? 'is-active' : ''} onClick={() => setKind(item)}>
                  {KIND_META[item]?.label || item}
                </button>
              ))}
            </div>
          ) : null}

          <div className="auditee-notifications-list">
            {loading ? <div className="workspace-list">{Array.from({ length: 4 }).map((_, index) => <div key={index} className="workspace-skeleton-row"><div className="workspace-skeleton-line workspace-skeleton-line-lg" /><div className="workspace-skeleton-line" /></div>)}</div> : (data.groups || []).length === 0 ? <EmptyState unreadOnly={unreadOnly} /> : data.groups.map((group) => (
              <div key={group.key} className="workspace-notification-group">
                <div className="workspace-notification-group-title"><span>{uiText(group.label)}</span><span className="workspace-notification-group-count">{group.items.length}</span></div>
                <div className="workspace-notifications-list">
                  {group.items.map((item) => <NotificationRow key={item.id} item={item} onOpen={openItem} isAuditee />)}
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="workspace-page">
      <PageIntro
        eyebrow={uiText("Центр уведомлений")}
        title={uiText("Уведомления")}
        description={uiText("Назначения, ответы и изменения статусов в единой ленте.")}
        aside={<div className="workspace-side-actions"><button type="button" className="btn btn-outline" onClick={load} disabled={loading}><RefreshCw size={15} /> {loading ? uiText("Обновляем…") : uiText("Обновить")}</button><button type="button" className="btn btn-primary" onClick={markAllRead} disabled={!data.unread_count}><CheckCheck size={15} /> {data.unread_count ? uiText("Прочитать всё") : uiText("Всё прочитано")}</button></div>}
      >
        <HelpText>{uiText("Фильтры помогают быстро найти непрочитанные события нужного типа.")}</HelpText>
        <QuickActions>
          {canViewAssignments ? <Link className="btn btn-outline" to="/assignment-notifications">{uiText("Мои задания")}</Link> : null}
          <Link className="btn btn-outline" to="/dashboard">{uiText("Обзор")}</Link>
        </QuickActions>
      </PageIntro>

      {error ? <div className="alert alert-error">{error}</div> : null}

      <div className="workspace-stats-grid">
        <div className="workspace-stat workspace-stat-danger"><div className="workspace-stat-value">{data.unread_count || 0}</div><div className="workspace-stat-label">{uiText("Непрочитанные")}</div><div className="workspace-stat-hint">{uiText("Все новые события, которые вы ещё не открывали")}</div></div>
        {filterOptions.filter((item) => item !== 'all').slice(0, 3).map((item) => (
          <div className="workspace-stat" key={item}><div className="workspace-stat-value">{data.kind_stats?.[item]?.unread || 0}</div><div className="workspace-stat-label">{KIND_META[item]?.label || item}</div><div className="workspace-stat-hint">{uiText("Всего: ")}{data.kind_stats?.[item]?.total || 0}</div></div>
        ))}
      </div>

      <div className="card workspace-notifications-filter-panel">
        <div className="card-body">
          <div className="workspace-notifications-filter-heading">
            <div><div className="workspace-list-title">{uiText("Показывать события")}</div><div className="workspace-muted">{uiText("Выберите тип или оставьте только непрочитанные")}</div></div>
            <label className="workspace-inline-check">
              <input type="checkbox" checked={unreadOnly} onChange={(e) => setUnreadOnly(e.target.checked)} />
              <span>{uiText("Только непрочитанные")}</span>
            </label>
          </div>
          <div className="workspace-chip-row workspace-notifications-filters" aria-label={uiText("Тип уведомлений")}>
            {filterOptions.map((item) => (
              <button type="button" key={item} className={`workspace-segment workspace-segment-compact ${kind === item ? 'is-active' : ''}`} onClick={() => setKind(item)}>
                {KIND_META[item]?.label || item}
                {item !== 'all' ? <span className="workspace-segment-count">{data.kind_stats?.[item]?.unread || 0}</span> : null}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="card workspace-notifications-center-card">
        <div className="card-header workspace-notifications-center-header"><div><div className="card-title">{uiText("Лента событий")}</div><div className="workspace-muted">{uiText("Новые события выделены цветной полосой")}</div></div><Badge variant="neutral">{data.items?.length || 0}</Badge></div>
        <div className="card-body workspace-notifications-list-page">
          {loading ? <div className="workspace-list">{Array.from({ length: 5 }).map((_, index) => <div key={index} className="workspace-skeleton-row"><div className="workspace-skeleton-line workspace-skeleton-line-lg" /><div className="workspace-skeleton-line" /></div>)}</div> : (data.groups || []).length === 0 ? <EmptyState unreadOnly={unreadOnly} homeTo="/dashboard" homeLabel={uiText("Открыть обзор")} /> : data.groups.map((group) => (
            <div key={group.key} className="workspace-notification-group">
              <div className="workspace-notification-group-title"><span>{uiText(group.label)}</span><span className="workspace-notification-group-count">{group.items.length}</span></div>
              <div className="workspace-notifications-list">
                {group.items.map((item) => <NotificationRow key={item.id} item={item} onOpen={openItem} />)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
